/**
 * View Medicine Page - Display detailed view of a specific medicine record
 * Beautiful UI with proper alignment and styling
 * Mapped to backend Medicine.java entity with exact field names
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pill, Edit, Trash2, Calendar, Package, AlertCircle, FileText } from 'lucide-react';
import { medicineApi, type Medicine } from '../../services/gaushala/api';

export default function ViewMedicine() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [medicine, setMedicine] = useState<Medicine | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMedicine = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);

      try {
        const result = await medicineApi.getMedicineById(parseInt(id));

        if (result.success && result.data) {
          setMedicine(result.data);
        } else {
          setError(result.error || 'Medicine not found');
        }
      } catch (error) {
        console.error('Error fetching medicine:', error);
        setError('Failed to load medicine details');
      } finally {
        setLoading(false);
      }
    };

    fetchMedicine();
  }, [id]);

  const handleEdit = () => {
    navigate(`/gaushala/medicine/edit/${id}`);
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this medicine record?')) {
      return;
    }

    try {
      const result = await medicineApi.deleteMedicine(parseInt(id));

      if (result.success) {
        navigate('/gaushala/health-history');
      } else {
        alert(result.error || 'Failed to delete medicine');
      }
    } catch (error) {
      console.error('Error deleting medicine:', error);
      alert('Failed to delete medicine');
    }
  };

  const handleBack = () => {
    navigate('/gaushala/health-history');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatExpiryDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const isExpired = (dateString?: string): boolean => {
    if (!dateString) return false;
    try {
      const expiryDate = new Date(dateString);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return expiryDate < today;
    } catch {
      return false;
    }
  };

  const isExpiringSoon = (dateString?: string): boolean => {
    if (!dateString) return false;
    try {
      const expiryDate = new Date(dateString);
      const today = new Date();
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      return expiryDate >= today && expiryDate <= thirtyDaysFromNow;
    } catch {
      return false;
    }
  };

  const DetailField = ({ label, value, icon: Icon, className = '' }: { label: string; value: string | number; icon?: any; className?: string }) => (
    <div className={`bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-lg p-4 hover:shadow-md transition-shadow ${className}`}>
      <div className="flex items-start gap-3">
        {Icon && <Icon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</label>
          <div className="text-gray-900 font-semibold text-lg break-words">{value || '-'}</div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 text-lg">Loading medicine details...</p>
        </div>
      </div>
    );
  }

  if (error || !medicine) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Medicine Not Found</h1>
            <p className="text-gray-600 mt-2">{error || 'The requested medicine record could not be found.'}</p>
          </div>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to List
          </button>
        </div>
      </div>
    );
  }

  const expired = isExpired(medicine.expiryDate);
  const expiringSoon = isExpiringSoon(medicine.expiryDate);

  return (
    <div className="space-y-6 pb-8">
      {/* Top Navigation Bar */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to Medicine List
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">ID: {medicine.id}</span>
        </div>
      </div>

      {/* Hero Card with Medicine Title */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Pill className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold mb-2">{medicine.name}</h1>
              <p className="text-blue-100 flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-blue-300 rounded-full"></span>
                Medicine Inventory Record
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-blue-100 mb-1">Type</div>
            <div className="text-2xl font-semibold">{medicine.dosage}</div>
          </div>
        </div>
      </div>

      {/* Status Alerts */}
      {expired && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-5 flex items-start gap-4">
          <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-red-900 mb-1">Medicine Expired</h3>
            <p className="text-red-700 text-sm">This medicine has expired and should not be used. Please dispose of it properly.</p>
          </div>
        </div>
      )}

      {expiringSoon && !expired && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-5 flex items-start gap-4">
          <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-amber-900 mb-1">Expiring Soon</h3>
            <p className="text-amber-700 text-sm">This medicine will expire within 30 days. Consider using it soon or placing a reorder.</p>
          </div>
        </div>
      )}

      {/* Basic Information Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
            <Package className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <DetailField label="Medicine Name" value={medicine.name} icon={Pill} />
          <DetailField label="Batch Number" value={medicine.batchNumber || '-'} />
          <DetailField label="Manufacturer" value={medicine.manufacturer || '-'} />
          <DetailField label="Medicine Type" value={medicine.dosage} />
          <DetailField label="Unit of Measurement" value={medicine.unit} />
          <div className={`bg-gradient-to-br ${medicine.quantity < 10 ? 'from-red-50 to-red-100 border-red-200' : 'from-green-50 to-green-100 border-green-200'} border rounded-lg p-4`}>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Current Stock</label>
            <div className="flex items-end justify-between">
              <span className={`text-3xl font-bold ${medicine.quantity < 10 ? 'text-red-700' : 'text-green-700'}`}>
                {medicine.quantity}
              </span>
              <span className="text-gray-600 font-medium">{medicine.unit}</span>
            </div>
            {medicine.quantity < 10 && (
              <div className="mt-3 pt-3 border-t border-red-200">
                <span className="inline-block bg-red-200 text-red-800 text-xs font-semibold px-3 py-1 rounded-full">⚠ Low Stock Alert</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expiry & Storage Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 bg-amber-100 rounded-full flex items-center justify-center">
            <Calendar className="h-5 w-5 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Expiry & Dates</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`bg-gradient-to-br ${expired ? 'from-red-50 to-red-100 border-red-200' : expiringSoon ? 'from-amber-50 to-amber-100 border-amber-200' : 'from-green-50 to-green-100 border-green-200'} border rounded-lg p-4`}>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">Expiry Date</label>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className={`h-5 w-5 ${expired ? 'text-red-600' : expiringSoon ? 'text-amber-600' : 'text-green-600'}`} />
              <span className={`text-xl font-bold ${expired ? 'text-red-700' : expiringSoon ? 'text-amber-700' : 'text-green-700'}`}>
                {formatExpiryDate(medicine.expiryDate)}
              </span>
            </div>
            {expired && <span className="inline-block bg-red-200 text-red-800 text-xs font-semibold px-3 py-1 rounded-full">Expired</span>}
            {expiringSoon && !expired && <span className="inline-block bg-amber-200 text-amber-800 text-xs font-semibold px-3 py-1 rounded-full">Expiring Soon</span>}
            {!expired && !expiringSoon && <span className="inline-block bg-green-200 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">Valid</span>}
          </div>
          {medicine.createdAt && (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Created On</label>
              <p className="text-gray-900 font-semibold">{formatDate(medicine.createdAt)}</p>
            </div>
          )}
          {medicine.updatedAt && (
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
              <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">Last Updated</label>
              <p className="text-gray-900 font-semibold">{formatDate(medicine.updatedAt)}</p>
            </div>
          )}
        </div>
      </div>

      {/* Purpose Section */}
      {medicine.purpose && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
              <Pill className="h-5 w-5 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Purpose & Usage</h2>
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 border border-indigo-200 rounded-lg p-6">
            <p className="text-indigo-900 leading-relaxed text-lg">{medicine.purpose}</p>
          </div>
        </div>
      )}

      {/* Description Section */}
      {medicine.description && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 bg-cyan-100 rounded-full flex items-center justify-center">
              <FileText className="h-5 w-5 text-cyan-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Description</h2>
          </div>
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-6">
            <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{medicine.description}</p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap items-center gap-4 pt-4">
        <button
          onClick={handleEdit}
          className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-md font-semibold text-lg"
        >
          <Edit className="h-5 w-5" />
          Edit Medicine
        </button>
        <button
          onClick={handleDelete}
          className="flex items-center gap-2 px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all transform hover:scale-105 shadow-md font-semibold text-lg"
        >
          <Trash2 className="h-5 w-5" />
          Delete Medicine
        </button>
        <button
          onClick={handleBack}
          className="flex items-center gap-2 px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-lg"
        >
          <ArrowLeft className="h-5 w-5" />
          Back to List
        </button>
      </div>
    </div>
  );
}
